import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../../i18n';
import MarketingHeader from '../common/MarketingHeader';
import MarketingFooter from '../common/MarketingFooter';
import GuideCta, { FreeNote } from '../common/GuideCta';
import CtaBand from '../Home/CtaBand';
import { CATALOG, DEFAULT_KEYS } from '../../../components/admin/BudgetPanel/catalog';
import { autoAllocate, DraftPost } from '../../../components/admin/BudgetPanel/Onboarding/allocation';
import { BUDGET_SIGNUP_URL } from '../signup/startIntent';
import appImage from '../../../assets/budget-app.webp';
import {
  AVERAGE_BUDGET,
  AVERAGE_GUESTS,
  FAQ,
  FORGOTTEN,
  GUEST_COUNTS,
  PER_GUEST,
  READING_MINUTES,
  SAVINGS,
  STEPS,
  TOC,
  UPDATED,
} from './content';
import '../../../styles/marketing.css';
import { AUTHOR, AUTHOR_URL } from '../common/author';
import { AuthorBox, AuthorByline } from '../common/AuthorByline';
import '../../../styles/marketing-guide.css';

const TITLE = 'Budget mariage : combien ça coûte, et comment le répartir';
const eur = (n: number) => `${n.toLocaleString('fr-FR')} €`;
const updatedLabel = new Date(`${UPDATED}T12:00`).toLocaleDateString('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

// Répartition : postes proposés par défaut dans l'app, parts usuelles et exemple
// pour 20 000 € et 90 invités, calculé par la répartition automatique de l'app.
const EXAMPLE_TOTAL = 20000;
const POSTS: DraftPost[] = CATALOG.filter((c) => DEFAULT_KEYS.includes(c.key)).map((c) => ({
  id: c.key,
  key: c.key,
  name: c.name,
  hint: c.hint,
  weight: c.weight,
  priority: c.priority,
  perGuest: c.perGuest,
}));
const WEIGHT_SUM = POSTS.reduce((s, p) => s + p.weight, 0);
const EXAMPLE = autoAllocate(EXAMPLE_TOTAL, POSTS, AVERAGE_GUESTS, []);
const SHARES = POSTS.map((p) => ({ ...p, share: Math.round((p.weight / WEIGHT_SUM) * 100), amount: EXAMPLE[p.id] }));

// Budget indicatif selon le nombre d'invités (coût moyen par invité, arrondi à 500 €)
const BY_GUESTS = GUEST_COUNTS.map((n) => ({ guests: n, budget: Math.round((n * PER_GUEST) / 500) * 500 }));

// Données structurées : article + FAQ (résultats enrichis Google)
const LD = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: TITLE,
    dateModified: UPDATED,
    datePublished: '2026-10-15',
    inLanguage: 'fr',
    author: { '@type': 'Person', name: AUTHOR.name, url: AUTHOR_URL },
    publisher: { '@type': 'Organization', name: 'RSVP Please' },
    image: 'https://www.please-rsvp.com/og-image.png',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
]);

const BudgetCta: React.FC<{ title: string; text: string; label?: string }> = ({
  title,
  text,
  label = 'Créer mon budget gratuit',
}) => <GuideCta to={BUDGET_SIGNUP_URL} title={title} text={text} label={label} />;

// Article « Budget mariage » : coût moyen, budget selon le nombre d'invités,
// répartition par poste, méthode, économies ; appels à créer son budget dans l'app.
const BudgetGuidePage: React.FC = () => {
  const t = useT();
  return (
    <div className="mk-page">
      <MarketingHeader>
        <Link to="/login" className="mkt-btn ghost">
          {t('Se connecter')}
        </Link>
        <Link to="/signup" className="mkt-btn primary mk-header-signup">
          {t('Créer mon espace')}
        </Link>
      </MarketingHeader>

      <main className="mk-main" lang="fr">
        <article className="mg-article mk-container">
          <header className="mg-article-head">
            <span className="mk-eyebrow">Guide · Budget</span>
            <h1>{TITLE}</h1>
            <AuthorByline updatedLabel={updatedLabel} readingMinutes={READING_MINUTES} />
            <p className="mg-article-lead">
              Combien coûte un mariage en France, combien prévoir pour 50, 80 ou 100 invités, et comment répartir
              l’enveloppe entre le lieu, le traiteur, les photos et le reste ? Les chiffres de référence, la répartition
              poste par poste, et la méthode pour tenir votre budget jusqu’au jour J.
            </p>
          </header>

          <BudgetCta
            title="Votre budget, réparti en 1 minute"
            text="Indiquez votre enveloppe, votre nombre d’invités et vos priorités : le budget est réparti poste par poste, et vous suivez chaque dépense et chaque échéance."
          />

          <nav className="mg-toc" aria-labelledby="mg-toc-title">
            <p id="mg-toc-title" className="mg-toc-title">
              Sommaire
            </p>
            <ol>
              {TOC.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{s.label}</a>
                </li>
              ))}
            </ol>
          </nav>

          <section id="budget-moyen">
            <h2>Le budget moyen d’un mariage en France</h2>
            <p>
              Un mariage coûte en moyenne <b>{eur(AVERAGE_BUDGET)}</b> en France, pour environ {AVERAGE_GUESTS} invités,
              soit autour de <b>{PER_GUEST} € par invité</b>. C’est le chiffre du Rapport sur le secteur nuptial 2026 de
              Mariages.net, pour les mariages célébrés en 2025, hors bague et voyage de noces.
            </p>
            <p>
              C’est une moyenne : plus de 40 % des couples dépensent 20 000 € ou plus, d’autres font un très beau
              mariage avec bien moins. Trois facteurs font varier la facture : le <b>nombre d’invités</b>, la{' '}
              <b>région</b> (les grandes villes et les régions touristiques sont plus chères) et la <b>saison</b> (un
              samedi de juin coûte plus qu’un vendredi d’octobre).
            </p>
          </section>

          <section id="nombre-invites">
            <h2>Budget mariage selon le nombre d’invités</h2>
            <p>
              En partant du coût moyen par invité, voici un ordre de grandeur. Les petits mariages coûtent un peu plus
              par personne : photographe, tenues ou alliances ne dépendent pas du nombre d’invités.
            </p>
            <table className="mg-table">
              <thead>
                <tr>
                  <th scope="col">Invités</th>
                  <th scope="col">Budget indicatif</th>
                </tr>
              </thead>
              <tbody>
                {BY_GUESTS.map((r) => (
                  <tr key={r.guests}>
                    <td>Mariage de {r.guests} personnes</td>
                    <td>environ {eur(r.budget)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              Le traiteur se paie au couvert : pour un dîner assis, comptez souvent <b>80 à 150 € par personne</b>, hors
              boissons. C’est pour ça que la liste d’invités est le premier levier sur le budget.
            </p>
          </section>

          <section id="repartition">
            <h2>La répartition du budget, poste par poste</h2>
            <p>
              Voici les parts habituelles de chaque poste, et ce qu’elles donnent pour un budget de {eur(EXAMPLE_TOTAL)}{' '}
              et {AVERAGE_GUESTS} invités. Ajustez ensuite selon vos priorités : plus pour ce qui compte, moins pour le
              reste.
            </p>
            <table className="mg-table mg-table-split">
              <thead>
                <tr>
                  <th scope="col">Poste</th>
                  <th scope="col">Part</th>
                  <th scope="col">Pour {eur(EXAMPLE_TOTAL)}</th>
                </tr>
              </thead>
              <tbody>
                {SHARES.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.name}
                      <span className="mg-cell-hint">{p.hint}</span>
                    </td>
                    <td>{p.share} %</td>
                    <td>{eur(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              Le voyage de noces, le transport et les cadeaux invités viennent en plus, si vous les financez avec la
              même enveloppe.
            </p>
          </section>

          <figure className="mg-cta-figure is-wide">
            <img
              src={appImage}
              width={1200}
              height={679}
              loading="lazy"
              decoding="async"
              alt="Budget de mariage dans l’espace RSVP Please : budget global, montant engagé et payé, et suivi de chaque poste"
            />
            <figcaption>
              <p className="mg-cta-title">Cette répartition, faite pour vous</p>
              <p>
                Indiquez votre enveloppe, votre nombre d’invités et vos trois priorités : chaque poste reçoit sa part,
                et vous voyez d’un coup d’œil ce qui est engagé, payé, et ce qu’il reste.
              </p>
              <Link to={BUDGET_SIGNUP_URL} className="mkt-btn primary">
                Répartir mon budget gratuitement
              </Link>
              <FreeNote />
            </figcaption>
          </figure>

          <section id="etablir">
            <h2>Établir son budget de mariage en 5 étapes</h2>
            <ol className="mg-steps">
              {STEPS.map((s) => (
                <li key={s.title}>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <section id="suivre">
            <h2>Suivre ses dépenses : acomptes, échéances, soldes</h2>
            <p>
              Un budget ne se dépasse pas d’un coup, mais devis après devis. La plupart des prestataires demandent un{' '}
              <b>acompte</b> à la réservation (souvent 30 %), parfois un deuxième versement, puis le <b>solde</b> avant
              ou le jour du mariage. Notez pour chaque dépense ce qui est payé, ce qui reste, et la date de chaque
              échéance.
            </p>
            <p>
              Faites le point à chaque nouvelle signature : si un poste dépasse, prenez sur un autre plutôt que sur la
              marge des imprévus. Et préparez les soldes à régler le jour J à l’avance, avec la personne qui s’en
              chargera.
            </p>
            <BudgetCta
              title="Ne ratez plus une échéance"
              text="Chaque acompte et chaque solde à sa date, avec un rappel par e-mail avant chaque paiement. Le budget engagé se met à jour tout seul."
              label="Suivre mon budget gratuitement"
            />
          </section>

          <section id="economies">
            <h2>10 astuces pour réduire le budget de son mariage</h2>
            <ol className="mg-steps">
              {SAVINGS.map((s) => (
                <li key={s.title}>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <section id="oublis">
            <h2>Les dépenses qu’on oublie</h2>
            <p>Les postes qui font déraper un budget sont souvent ceux qu’on n’avait pas prévus :</p>
            {FORGOTTEN.map((f) => (
              <div key={f.title} className="mg-admin">
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
            <p>
              Pour ne rien oublier dans le bon ordre, voir aussi notre{' '}
              <Link to="/retroplanning-mariage">rétroplanning de mariage, mois par mois</Link>.
            </p>
          </section>

          <section id="faq">
            <h2>Questions fréquentes</h2>
            <div className="mg-faq">
              {FAQ.map(({ q, a }) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </section>

          <AuthorBox />

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: LD }} />
        </article>

        <CtaBand />
      </main>

      <MarketingFooter />
    </div>
  );
};

export default BudgetGuidePage;
